/**
 * Physical connections model real-world cable patching between device ports.
 *
 * Each device type provides ports via `getDevicePorts()`, which scans
 * its DeviceStateStore using OSC-style key paths. A physical connection
 * (wire) pairs one output port to one input port.
 */
// ─── Port provider ──────────────────────────────────────────
/**
 * Extract all ports a device provides by scanning its OSC-style
 * DeviceStateStore. Uses the same key patterns as storeToVideoHubState()
 * and friends in device-state-store.ts.
 */
export function getDevicePorts(connectionId, connectionType, store) {
    switch (connectionType) {
        case "videohub":
            return getVideoHubPorts(connectionId, store);
        case "atem":
            return getAtemPorts(connectionId, store);
        default:
            return [];
    }
}
function getVideoHubPorts(connectionId, store) {
    const ports = [];
    const v = store.values;
    const inputIndices = new Set();
    const outputIndices = new Set();
    for (const key of Object.keys(v)) {
        const inputMatch = key.match(/^\/inputs\/(\d+)\//);
        if (inputMatch)
            inputIndices.add(Number(inputMatch[1]));
        const outputMatch = key.match(/^\/outputs\/(\d+)\//);
        if (outputMatch)
            outputIndices.add(Number(outputMatch[1]));
    }
    for (const i of [...inputIndices].sort((a, b) => a - b)) {
        ports.push({
            connection_id: connectionId,
            port_type: "videohub_input",
            direction: "input",
            index: i,
            label: String(v[`/inputs/${i}/label`] ?? `Input ${i + 1}`),
        });
    }
    for (const i of [...outputIndices].sort((a, b) => a - b)) {
        ports.push({
            connection_id: connectionId,
            port_type: "videohub_output",
            direction: "output",
            index: i,
            label: String(v[`/outputs/${i}/label`] ?? `Output ${i + 1}`),
        });
    }
    return ports;
}
function getAtemPorts(connectionId, store) {
    const ports = [];
    const v = store.values;
    const inputIndices = new Set();
    const auxIndices = new Set();
    const meIndices = new Set();
    for (const key of Object.keys(v)) {
        const inputMatch = key.match(/^\/inputs\/(\d+)\//);
        if (inputMatch)
            inputIndices.add(Number(inputMatch[1]));
        const auxMatch = key.match(/^\/auxiliaries\/(\d+)\//);
        if (auxMatch)
            auxIndices.add(Number(auxMatch[1]));
        const meMatch = key.match(/^\/mixEffects\/(\d+)\//);
        if (meMatch)
            meIndices.add(Number(meMatch[1]));
    }
    for (const i of [...inputIndices].sort((a, b) => a - b)) {
        ports.push({
            connection_id: connectionId,
            port_type: "atem_input",
            direction: "input",
            index: i,
            label: String(v[`/inputs/${i}/longName`] ?? `Input ${i + 1}`),
        });
    }
    for (const i of [...auxIndices].sort((a, b) => a - b)) {
        ports.push({
            connection_id: connectionId,
            port_type: "atem_aux",
            direction: "output",
            index: i,
            label: String(v[`/auxiliaries/${i}/longName`] ?? `Aux ${i + 1}`),
        });
    }
    for (const i of [...meIndices].sort((a, b) => a - b)) {
        ports.push({
            connection_id: connectionId,
            port_type: "atem_program",
            direction: "output",
            index: i,
            label: `Program ${i + 1}`,
        });
    }
    return ports;
}
